import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { ClassNamesConfig, GroupBase } from "react-select";
import AsyncSelect from "react-select/async";

import { Label } from "@/components/ui/label";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { getSessionRoomSystems } from "@/features/room-systems/api/get-room-systems";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { cn, getDateWithTime } from "@/lib/utils";

import { AddSessionFormData } from "../types/add-session-schema";
import { RoomSystemOption } from "../types/room-system-option";

import { RoomSystemSelectOption } from "./room-system-select-option";

export function RoomSystemsSearchElement() {
  const form = useFormContext<AddSessionFormData>();
  const { organisation } = useOrganisation();
  const [value, setValue] = useState<string>("");

  const addRoomSystem = (roomSystem: RoomSystemOption) => {
    form.setValue("roomSystems", [
      ...(form.getValues("roomSystems") || []),
      roomSystem,
    ]);
    form.trigger("roomSystems");
  };

  const roomSystemsError = form.formState.errors.roomSystems;
  const reactSelectClassNames = createClassNames({
    control: () => {
      return cn(!!roomSystemsError && "border-destructive");
    },
  });

  const selectedRoomSystems = form.getValues("roomSystems");

  const loadOptions = async (input: string): Promise<RoomSystemOption[]> => {
    const startDate = form.getValues("startDate");
    const startTime = form.getValues("startTime");
    const endTime = form.getValues("endTime");

    const from = getDateWithTime(startDate, startTime);
    const to = getDateWithTime(startDate, endTime);

    const roomSystems = await getSessionRoomSystems({
      organisationId: organisation.id,
      pageSize: 20,
      pageIndex: 0,
      search: input,
      sessionStart: from.toISOString(),
      sessionEnd: to.toISOString(),
    });

    const filteredRoomSystems = roomSystems.items.filter(
      (roomSystem) =>
        !selectedRoomSystems.some((rs) => rs.id === roomSystem.id),
    );

    return filteredRoomSystems.map((roomSystem) => ({
      id: roomSystem.id,
      name: roomSystem.displayName,
      organisationId: roomSystem.organisation.id,
      organisationName: roomSystem.organisation.name,
      conflictingSessions: roomSystem?.sessions?.conflictingSessions || [],
    }));
  };

  return (
    <div className="mb-4 space-y-4">
      <div className="space-y-2">
        <Label>Room Systems</Label>
        <AsyncSelect<RoomSystemOption>
          unstyled
          components={{
            DropdownIndicator: SearchIndicator,
            ClearIndicator: () => null,
            Option: RoomSystemSelectOption,
            IndicatorSeparator: () => null,
          }}
          ref={form.register("roomSystems").ref}
          classNames={
            reactSelectClassNames as ClassNamesConfig<
              RoomSystemOption,
              false,
              GroupBase<RoomSystemOption>
            >
          }
          placeholder="Type room system name..."
          noOptionsMessage={() => "Type room system name..."}
          loadOptions={loadOptions}
          isDisabled={form.formState.isSubmitting}
          inputValue={value}
          onInputChange={(value) => {
            setValue(value);
          }}
          value={null}
          getOptionLabel={(option) => option.name}
          onChange={(value) => {
            if (value) {
              addRoomSystem(value);
              setValue(""); // Clear the input once added
              return null; // Prevent adding to internal state
            }
          }}
        />
        {roomSystemsError && (
          <p className="text-sm font-medium text-destructive">
            {roomSystemsError.message}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          This includes any room systems of any organisations within the group
        </p>
      </div>
    </div>
  );
}
